/**
 * Mapeamento + fetch puro da v2 `/monitoramento` → leitura — GUPM-126 (rework v2-only).
 * ------------------------------------------------------------------------------------
 * TypeScript PURO (sem React/Supabase/IO além do `fetch` injetável). Converte os
 * registros crus da v2 corporativa (`gpxMonitorCorp/v2/monitoramento`, auth `X-API-Key`)
 * em: (a) `LandingRow` verbatim ("ingerir tudo" → `gpx_monitoramento_raw`) e (b)
 * `GpxReadingRecord[]` curado (1 valor classificável por canal → `readings`).
 *
 * Regra inviolável: SÓ GET (nunca POST/PUT/PATCH/DELETE). A `X-API-Key` vai no header,
 * NUNCA na URL/query/log/erro. Contrato: docs/reference/gpx-api/api-monitoramento-v2.md.
 *
 * Extensão `.ts` explícita no import: o grafo do edge runtime (Deno) resolve até imports
 * type-only e não infere extensão. `allowImportingTsExtensions` cobre Vite/tsc.
 */
import type { Canal } from '../types.ts';

/** Base da v2 corporativa. Só-GET. */
export const V2_BASE = 'https://fosfatados.itgsistemas.com.br/gpxMonitorCorp/v2';
export const ESTRUTURAS = ['A', 'B', 'C'] as const;
/** Tipos de leitura da rota de monitoramento (TIL, não TLT). */
export const TIPOS_LEITURA = ['INA', 'PZ', 'TIL', 'RL', 'MV', 'PLV', 'EM'] as const;

/**
 * Cadência de publicação por tipo, em minutos — MEDIDA em produção (14 dias, agosto/2026).
 * Indexada pelo tipo da **API** (`TIL`), não pelo enum do banco (`TILT`).
 *
 * Era comentário e virou dado no GUPM-305. Enquanto foi só comentário, a janela de frescor
 * derivou dele errado: dizia "~2× a cadência" e o `TIL` estava a 2h, ou seja 4× — o único
 * fora da regra, no sensor de ruptura. Com a janela derivada daqui, divergir de novo é
 * impossível.
 *
 * EM remedido no GUPM-308: publicava de hora em hora em setembro e, em 18–20/10/2026, só às
 * 00/06/12/18 h (teste ponta a ponta com a GPX real). Com 60 min, cada publicação abria um
 * episódio falso de sem sinal 1h45 depois.
 */
const CADENCIA_MIN: Record<string, number> = {
  TIL: 30, EM: 360, RL: 240, PLV: 240, INA: 360, PZ: 360, MV: 720,
};

/**
 * Default conservador para tipo sem cadência medida: 12h → janela de 12h45. É o maior
 * valor da tabela acima, então `maiorJanelaFrescorMin()` continua sendo o teto da frota
 * mesmo que apareça um tipo novo que ninguém cadastrou aqui.
 */
const CADENCIA_DEFAULT_MIN = 720;

/**
 * Registro cru da v2 `/monitoramento`. Fixos + dinâmicos por tipo (campos `null`
 * são OMITIDOS pela API). Index signature preserva o shape esparso verbatim.
 */
export interface V2MonitoramentoRaw {
  instrumento: string;
  tipoInstrumento: string;
  dataHoraLeitura: string; // ISO 8601 UTC
  [campo: string]: unknown;
}

/** Leitura curada (intermediária, antes de virar linha em `readings`). Sem `online` — status é derivado no writer. */
export interface GpxReadingRecord {
  dam: 'A' | 'B' | 'C';
  code: string; // pelado, sem o prefixo UPM-{dam}-
  canal: Canal | null; // null = nível d'água; resultante/temperatura = tilt
  value: number;
  ts: string; // = dataHoraLeitura
}

/** Linha do landing cru `gpx_monitoramento_raw` (payload verbatim + derivadas). */
export interface LandingRow {
  area: string;
  estrutura: 'A' | 'B' | 'C';
  tipo: string;
  instrumento: string;
  dataHoraLeitura: string;
  payload: V2MonitoramentoRaw;
}

/** Dependências injetáveis do fetcher (key + fetch + janela p/ teste). */
export interface FetchMonitoramentoDeps {
  apiKey: string;
  dataDe: string; // dd-MM-yyyy
  dataAte: string; // dd-MM-yyyy
  fetchFn?: typeof fetch;
  baseUrl?: string;
  area?: string;
  estruturas?: readonly ('A' | 'B' | 'C')[];
  tipos?: readonly string[];
}

/** Coerção segura → número finito ou `null` (a v2 pode omitir/trazer não-número). */
function num(v: unknown): number | null {
  return typeof v === 'number' && Number.isFinite(v) ? v : null;
}

/** Quebra `nomeInstrumento` (`"UPM-B-INA-01"`) em barragem + código pelado. */
export function parseNomeInstrumento(nome: string): { dam: 'A' | 'B' | 'C'; code: string } {
  const m = /^UPM-([ABC])-(.+)$/.exec(nome.trim());
  if (!m) {
    throw new Error(`nomeInstrumento fora do padrão UPM-{dam}-{code}: "${nome}"`);
  }
  return { dam: m[1] as 'A' | 'B' | 'C', code: m[2]!.trim() };
}

// GUPM-270: `resultanteTilt` (√(axisA² + axisB²)) foi removida. Era conta NOSSA — a
// v2 manda `axisA` e `axisB` — e não tem significado físico para eixos
// independentes: colapsa dois movimentos distintos num número só e perde o sinal,
// que é a direção. Os eixos entram assinados e a régua da Carta é por eixo.

/**
 * Achata um registro v2 em 0+ leituras curadas (dispatch por `tipoInstrumento`).
 * - INA/PZ/RL/MV/PLV → 1 leitura canal null (valor classificável do tipo).
 * - TIL → eixo_a + eixo_b assinados + temperatura (GUPM-270; nunca x/y/z).
 * - EM (e tipos desconhecidos) → nenhuma leitura curada (só landing).
 */
export function mapRecordToReadings(raw: V2MonitoramentoRaw): GpxReadingRecord[] {
  const { dam, code } = parseNomeInstrumento(raw.instrumento);
  const ts = raw.dataHoraLeitura;
  const out: GpxReadingRecord[] = [];
  const push = (canal: Canal | null, value: number | null): void => {
    if (value !== null) out.push({ dam, code, canal, value, ts });
  };

  switch (raw.tipoInstrumento) {
    case 'INA':
    case 'PZ':
      push(null, num(raw.cotaAgua));
      break;
    case 'RL':
      push(null, num(raw.naBarragemCota));
      break;
    case 'MV':
      push(null, num(raw.metrosCubicosHora));
      break;
    case 'PLV':
      push(null, num(raw.precipitacaoMM));
      break;
    case 'TIL': {
      // Eixos INDEPENDENTES e ASSINADOS (GUPM-270). Cada um é uma grandeza com sua
      // própria faixa na Carta; o `push` já ignora `null`, então eixo omitido pela
      // v2 não gera linha — e nunca vira 0, que seria a leitura mais
      // tranquilizadora possível para um tiltímetro (GUPM-248: omissão é buraco).
      push('eixo_a', num(raw.axisA));
      push('eixo_b', num(raw.axisB));
      push('temperatura', num(raw.temperature));
      break;
    }
    // EM e desconhecidos: só landing (sem valor único/Carta).
    default:
      break;
  }
  return out;
}

/**
 * Origem de reserva de `estrutura`/`tipo` quando o registro não permite deduzi-las:
 * o próprio request (quem pediu `estrutura/B` `tipo/INA` sabe o que pediu).
 */
export interface OrigemRequest {
  estrutura: 'A' | 'B' | 'C';
  tipo: string;
}

/**
 * Registro v2 → linha do landing cru (payload verbatim + derivadas).
 *
 * A estrutura vem do nome quando ele casa com `UPM-{A|B|C}-{code}`; quando não
 * casa — e a v2 tem cadastro assim, `UPM-GEOKON-TESTE` é instrumento de teste
 * do fornecedor — cai no `origem` do request em vez de lançar (GUPM-226). O
 * landing é a camada do "salva tudo, decide depois": derrubar a busca inteira
 * por causa de um nome estranho perderia os registros bons do mesmo lote.
 */
export function mapRecordToLanding(
  raw: V2MonitoramentoRaw,
  area: string,
  origem?: OrigemRequest,
): LandingRow {
  let estrutura: 'A' | 'B' | 'C';
  try {
    estrutura = parseNomeInstrumento(raw.instrumento).dam;
  } catch (e) {
    if (!origem) throw e; // sem nome utilizável e sem request: não dá pra inventar
    estrutura = origem.estrutura;
  }
  return {
    area,
    estrutura,
    // `tipoInstrumento` é obrigatório no contrato, mas a rota de inventário já
    // devolveu registro sem ele (UPM-C-TLT-01) — a coluna é NOT NULL.
    tipo: raw.tipoInstrumento || (origem?.tipo ?? ''),
    instrumento: raw.instrumento,
    dataHoraLeitura: raw.dataHoraLeitura,
    payload: raw,
  };
}

/** Cadência esperada de publicação (min) por tipo da **API** — `TIL`, não `TILT`. */
export function cadenciaEsperadaMin(tipo: string): number {
  return CADENCIA_MIN[tipo] ?? CADENCIA_DEFAULT_MIN;
}

/**
 * Tolerância de ENTREGA por tipo da API, em minutos: quanto a leitura pode chegar atrasada
 * sem que o instrumento seja acusado de mudo.
 *
 * É aditiva, não proporcional à cadência — o atraso é do transporte (API + nosso poll), e
 * não cresce com a cadência do instrumento. A regra anterior ao GUPM-305 (`2 × cadência`)
 * dava 12h de folga ao MV e 4× a cadência ao TIL, sem razão física para nenhum dos dois.
 *
 * **TIL = 20** (GUPM-305). Medido em 7 dias × 10 tiltímetros (2.908 intervalos normais,
 * queda de 21/08 excluída): janela de 40 min → 16 falsos; 45 → 10; 50 → 10; 60 → 7. É o
 * sensor de ruptura — a folga mínima que não vira alarme diário.
 *
 * **Demais = 45** (GUPM-308). Até então os 20 do TIL valiam para todos, e em 14 dias de
 * produção (out/2026) isso abriu ~990 episódios falsos (INA 460, EM 275, PZ 196). O atraso
 * de publicação da GPX é BIMODAL: ou chega em minutos, ou entre 30 e 40 min — e nunca passou
 * de 40,4 min fora de silêncio real. O 2º modo não é aleatório: 9 instrumentos do gateway
 * 21294 (A-INA-10A, B-INA-02/03/10/13, B-PZ-03/11/13, C-INA-07) chegam assim em ~85% das
 * publicações (Auditoria da API, 15/10/2026). 45 = esse teto + margem; com ela sobram 24
 * episódios em 14 dias, todos silêncio real. Tipo desconhecido cai aqui: o lado de menos
 * alarme falso, como a cadência default.
 *
 * Recalibrar com os dados da rodada de 1 min: a medição acima tem resolução de 10 min (o
 * cron era `*\/10`), então cada número é um teto.
 */
const TOLERANCIA_ENTREGA_MIN: Record<string, number> = { TIL: 20 };
const TOLERANCIA_ENTREGA_DEFAULT_MIN = 45;

/** Tolerância de entrega (min) por tipo da **API** — `TIL`, não `TILT`. */
export function toleranciaEntregaMin(tipo: string): number {
  return TOLERANCIA_ENTREGA_MIN[tipo] ?? TOLERANCIA_ENTREGA_DEFAULT_MIN;
}

/** Janela de frescor (min) para derivar `is_online`: cadência + tolerância de entrega do tipo. */
export function janelaFrescorMin(tipo: string): number {
  return cadenciaEsperadaMin(tipo) + toleranciaEntregaMin(tipo);
}

/**
 * Período pedido à v2 em cada rodada (horas). Mora aqui, e não na Edge Function, porque
 * a detecção de perda de sinal DEPENDE dele: a ingestão só julga instrumento que veio no
 * lote, e o lote é o que a v2 devolve para este período. Quem sai da janela para de ser
 * avaliado e congela o `is_online` que tinha.
 *
 * Precisa ser maior que `maiorJanelaFrescorMin()` — invariante afirmado em teste.
 */
export const JANELA_REQUISICAO_HORAS = 48;

/**
 * A maior janela de frescor da frota (hoje o MV, de cadência 12 h). Inclui o default: um
 * tipo que ninguém cadastrou cai em cadência e tolerância default e não pode passar do teto.
 */
export function maiorJanelaFrescorMin(): number {
  return Math.max(
    CADENCIA_DEFAULT_MIN + TOLERANCIA_ENTREGA_DEFAULT_MIN,
    ...Object.keys(CADENCIA_MIN).map(janelaFrescorMin),
  );
}

/**
 * Quantas publicações esperadas não vieram entre `lastReadingAt` e `agora`.
 * Nunca negativo: deriva de relógio (agora antes da leitura) devolve 0, não uma
 * contagem invertida que abriria episódio ao contrário.
 */
export function publicacoesPerdidas(lastReadingAt: string, agora: Date, tipo: string): number {
  const idadeMin = (agora.getTime() - new Date(lastReadingAt).getTime()) / 60_000;
  if (!Number.isFinite(idadeMin) || idadeMin <= 0) return 0;
  return Math.floor(idadeMin / cadenciaEsperadaMin(tipo));
}

/** O que fazer com o episódio de perda de sinal deste instrumento nesta rodada. */
export type DecisaoEpisodio = 'abre' | 'fecha' | 'nada';

/**
 * Decide o episódio comparando o status ANTERIOR com o derivado agora. Existe para que o
 * registro seja por **episódio** e não por tick: 84 instrumentos × 144 ciclos/dia
 * inundariam o `audit_log`, e o RT precisa de "caiu às 04:30, voltou às 13:40", não de
 * 19 linhas dizendo a mesma coisa.
 *
 * `anterior === null` (instrumento recém-cadastrado, status nunca derivado) **não** é
 * tratado como `false`: se fosse, todo cadastro novo com leitura fresca geraria um
 * "sinal restabelecido" de um silêncio que nunca existiu.
 */
export function decidirEpisodio(
  { anterior, atual }: { anterior: boolean | null; atual: boolean },
): DecisaoEpisodio {
  if (atual) return anterior === false ? 'fecha' : 'nada';
  return anterior === false ? 'nada' : 'abre';
}

/** Piso de amostra para acusar o feed em vez dos instrumentos. */
const MIN_LOTE_QUEDA_FEED = 5;
/** Fração do lote defasada a partir da qual a causa provável é o feed, não os sensores. */
const FRACAO_QUEDA_FEED = 0.5;

/**
 * Se **metade ou mais** de um grupo está defasada, a causa provável é o transporte, não
 * os instrumentos — registra-se um evento de feed e nenhum episódio individual.
 *
 * Decide sobre **um grupo homogêneo**, nunca sobre o lote inteiro: quem escolhe o
 * recorte é `tiposEmQuedaDeFeed`, e o docstring de lá explica por que o recorte é o tipo.
 *
 * Minoria defasada é o contrário: foi o instrumento que caiu, não o feed (caso real do
 * TIL-05 — cinco irmãos publicando 19 vezes, ele zero). O piso de amostra evita que um
 * grupo pequeno todo defasado vire acusação forte a partir de amostra fraca.
 */
export function ehQuedaDeFeed(statusOnline: readonly boolean[]): boolean {
  if (statusOnline.length < MIN_LOTE_QUEDA_FEED) return false;
  const defasados = statusOnline.filter((online) => !online).length;
  return defasados >= statusOnline.length * FRACAO_QUEDA_FEED;
}

/** Instrumento avaliado numa rodada, reduzido ao que a guarda precisa saber. */
export interface AlvoStatus {
  /** Tipo da **API** (`TIL`, `INA`, …), não o enum do banco. */
  tipo: string;
  isOnline: boolean;
}

/**
 * Tipos cujo grupo está em queda de feed nesta rodada.
 *
 * O recorte é o **tipo** porque é assim que falamos com o fornecedor: a rodada faz 21
 * requisições, uma por combo estrutura×tipo. Quando um endpoint devolve 502, quem fica
 * sem dado é o grupo daquele endpoint — a falha de transporte chega agrupada por tipo,
 * não espalhada pelo lote.
 *
 * Medir sobre o lote inteiro era um erro de denominador, e a medição de produção mostra
 * o tamanho dele: a rodada traz ~59 instrumentos (28 INA, 14 PZ, 10 TIL, 7 avulsos), e
 * em 7 dias o pico de defasados simultâneos foi **4** — 6,8%. Nenhum instante chegou
 * perto da maioria. A guarda existia e nunca disparava; e o caso que ela deveria cobrir,
 * os 10 tiltímetros mudos de uma vez, dava 17% e saía como 10 acusações individuais.
 *
 * ATENÇÃO ao alcance: só decide sobre um lote que EXISTE. Quando a rodada morre antes de
 * montar o lote — 20-21/08/2026, a v2 em HTTP 502/408 e a função abortando no primeiro
 * dos 21 endpoints, 18 h sem inserir nada — não há grupo, não há evento. Cobrir esse
 * buraco é vigiar a **inserção**, fora daqui. O `cron` não ajuda: reportou 6607
 * execuções e zero falhas o tempo todo, porque `net.http_post` só enfileira.
 *
 * E o grupo ENCOLHE com o tempo: instrumento cuja última leitura sai da janela pedida
 * some do retorno da v2, some de `alvos` e deixa de contar no denominador. O corte é por
 * DIA, não por hora (`dataDe` é `dd-MM-yyyy`), então a cobertura real vai de
 * `JANELA_REQUISICAO_HORAS` a ~72 h e a queda acontece na virada da meia-noite. Medido em
 * produção em 25/08/2026 pelo `instruments.updated_at` (que desde o GUPM-308 só avança quando o
 * status muda — a pista deixou de valer): o INA-12A (última leitura 20/08
 * 12:00) parou de ser avaliado em 22/08 23:50. Numa queda longa de endpoint o grupo vai
 * secando até cruzar `MIN_LOTE_QUEDA_FEED` para baixo — a
 * guarda se desliga sozinha justamente na falha mais grave, e voltam as acusações
 * individuais. Aqui não dá pra consertar: quem sabe quem DEVERIA ter vindo é o cadastro,
 * não o lote. Some com o buraco acima na mesma vigia da inserção.
 */
export function tiposEmQuedaDeFeed(alvos: readonly AlvoStatus[]): Set<string> {
  const porTipo = new Map<string, boolean[]>();
  for (const { tipo, isOnline } of alvos) {
    const grupo = porTipo.get(tipo);
    if (grupo) grupo.push(isOnline);
    else porTipo.set(tipo, [isOnline]);
  }
  const emQueda = new Set<string>();
  for (const [tipo, statusOnline] of porTipo) {
    if (ehQuedaDeFeed(statusOnline)) emQueda.add(tipo);
  }
  return emQueda;
}

// ── Rodada rápida (GUPM-308) ─────────────────────────────────────────────────────────

/** Um endpoint da v2: estrutura × tipo da **API** (`TIL`, não `TILT`). */
export interface Combo {
  estrutura: 'A' | 'B' | 'C';
  tipo: string;
}

/** Os 21 combos que a varredura completa pede (3 estruturas × 7 tipos). */
export const TODOS_OS_COMBOS: readonly Combo[] = ESTRUTURAS.flatMap((estrutura) =>
  TIPOS_LEITURA.map((tipo) => ({ estrutura, tipo })),
);

/** Data no formato `dd-MM-yyyy` (UTC) exigido pela query da v2. */
export function formatarDataV2(d: Date): string {
  const dd = String(d.getUTCDate()).padStart(2, '0');
  const mm = String(d.getUTCMonth() + 1).padStart(2, '0');
  return `${dd}-${mm}-${d.getUTCFullYear()}`;
}

/**
 * Recuo do início do período da rápida. Cobre as duas leituras possíveis do corte por dia
 * da v2: em UTC (a leitura das 23:30 ainda chegando depois da meia-noite) e em horário de
 * Brasília (UTC−3), que o contrato NÃO esclarece — ele só diz que o RETORNO vem em UTC. Se
 * a v2 cortar em BRT, pedir o dia UTC entre 00:00 e 03:00 UTC devolveria 204 em todo combo.
 */
const RECUO_PERIODO_RAPIDA_H = 4;

/**
 * Período pedido pela rodada rápida: do dia de `agora − 4 h` até hoje (UTC).
 *
 * A v2 corta por DIA e responde 204, não 400, para data malformada ou intervalo invertido:
 * um erro aqui vira "sem dados" em silêncio, por isso a regra mora num lugar só e tem teste
 * de borda. Entre 00:00 e 04:00 UTC a rápida pede dois dias — o descarte do já conhecido
 * tira o excesso antes de qualquer escrita.
 */
export function periodoRodadaRapida(agora: Date): { dataDe: string; dataAte: string } {
  return {
    dataDe: formatarDataV2(new Date(agora.getTime() - RECUO_PERIODO_RAPIDA_H * 3_600_000)),
    dataAte: formatarDataV2(agora),
  };
}

/**
 * Tipos que a rodada rápida acompanha: os que rendem leitura curada (e por isso têm
 * `last_reading_at` que avança) e têm cadência medida.
 *
 * EM fica de fora de propósito: não rende leitura nenhuma (17 campos, nenhum valor único
 * classificável — GUPM-228), então o `last_reading_at` dele nunca avança e o combo ficaria
 * devido para sempre. A estação segue na varredura completa.
 */
const TIPOS_DA_RAPIDA: ReadonlySet<string> = new Set(['TIL', 'INA', 'PZ', 'RL', 'PLV', 'MV']);

/** Até quantos minutos depois da publicação esperada a rápida pergunta a cada minuto. */
const RAPIDA_TODO_MINUTO_ATE_MIN = 10;
/** Depois disso, de quantos em quantos minutos ela volta a perguntar. */
const RAPIDA_BACKOFF_MIN = 5;

/** Instrumento do cadastro reduzido ao que decide se o combo dele está devido. */
export interface InstrumentoParaDevido {
  estrutura: 'A' | 'B' | 'C';
  /** Tipo da **API** — quem chama traduz o enum do banco (`tipoApiDeInstrumento`). */
  tipoApi: string;
  ativo: boolean;
  /** GUPM-307 — declarado fora de serviço pelo RT: silêncio conhecido, não se espera publicação. */
  foraDeServico: boolean;
  lastReadingAt: Date | null;
}

/** O instrumento tem publicação devida e ainda não recebida neste minuto? */
function publicacaoDevida(inst: InstrumentoParaDevido, agora: Date): boolean {
  if (!inst.ativo || inst.foraDeServico || inst.lastReadingAt === null) return false;
  if (!TIPOS_DA_RAPIDA.has(inst.tipoApi)) return false;

  // Minutos INTEIROS desde a última leitura: a rodada roda ~20 s depois do minuto (pg_cron
  // → pg_net → boot da função). A borda ainda pode escorregar um tick quando a leitura tem
  // segundos (o TIL-05 às vezes publica fora do relógio) — custa no máximo 1 min de latência,
  // nunca um GET a mais por minuto.
  const decorrido = Math.floor((agora.getTime() - inst.lastReadingAt.getTime()) / 60_000);
  const cadencia = cadenciaEsperadaMin(inst.tipoApi);

  // Começa no minuto do carimbo, nunca antes: a leitura de `hh:00` não existe às `hh:59:20`.
  // Começar 1 min antes custou ~17% dos GETs da rápida sem trazer nada (E2E de 20/10/2026).
  if (decorrido < cadencia) return false;
  // Passou da janela de frescor: é silêncio, não atraso. Quem pergunta é a completa —
  // sem este corte um sensor mudo há dias manteria o combo devido para sempre.
  if (decorrido > janelaFrescorMin(inst.tipoApi)) return false;
  // A maioria chega em minutos (TIL: mediana de 1,6 min, Auditoria da API, 15/10/2026).
  if (decorrido <= cadencia + RAPIDA_TODO_MINUTO_ATE_MIN) return true;
  // Os lentos do gateway 21294 chegam com 30–40 min: perguntar todo minuto seriam ~40 GETs
  // por publicação. De 5 em 5 min a INA cai de ~860 para ~280 GETs/dia (simulação de 7 dias).
  return (decorrido - cadencia - RAPIDA_TODO_MINUTO_ATE_MIN) % RAPIDA_BACKOFF_MIN === 0;
}

/**
 * Combos estrutura×tipo que a rodada rápida deve pedir agora — GUPM-308.
 *
 * Pedir os 21 combos todo minuto seriam 30.240 GETs/dia numa API sem rate limit
 * documentado. Um combo só entra quando ALGUM instrumento dele já devia ter publicado e
 * ainda não publicou: simulado sobre 7 dias de produção, ~2.000 GETs/dia a mais que hoje.
 *
 * Instrumento novo (sem `last_reading_at`) nunca torna combo devido — ele aparece pela
 * varredura completa, que continua pedindo os 21 combos (lição do INA-28, descartado por 2
 * meses por falta de cadastro).
 */
export function combosDevidos(insts: readonly InstrumentoParaDevido[], agora: Date): Combo[] {
  const vistos = new Set<string>();
  const out: Combo[] = [];
  for (const inst of insts) {
    if (!publicacaoDevida(inst, agora)) continue;
    const chave = `${inst.estrutura}|${inst.tipoApi}`;
    if (vistos.has(chave)) continue;
    vistos.add(chave);
    out.push({ estrutura: inst.estrutura, tipo: inst.tipoApi });
  }
  return out;
}

/** Chave `${dam}|${code}` do instrumento a partir do nome da v2; `null` se fora do padrão. */
function chaveDoNome(nome: string): string | null {
  try {
    const { dam, code } = parseNomeInstrumento(nome);
    return `${dam}|${code}`;
  } catch {
    return null;
  }
}

/**
 * Descarta, em memória, o que o banco já tem: registro com `ts ≤ last_reading_at` do
 * instrumento — GUPM-308, só na rodada rápida.
 *
 * A rápida pede o dia inteiro do combo e recebe de volta centenas de registros para trazer
 * um ou dois novos. Sem o descarte, cada minuto mandaria tudo ao upsert para o banco
 * ignorar. O que fica para trás (canal publicado fora de ordem, retroativo) é trabalho da
 * varredura completa, que não descarta.
 *
 * Comparação por INSTANTE: `last_reading_at` volta do banco como `+00:00` e a v2 manda `Z`.
 * Instrumento sem `last_reading_at` conhecido e nome fora do padrão passam intactos.
 * `descartados` conta registros do landing — o que deixou de ir ao banco.
 */
export function descartarJaConhecidos(
  input: { landing: LandingRow[]; readings: GpxReadingRecord[] },
  ultimaPorChave: ReadonlyMap<string, Date>,
): { landing: LandingRow[]; readings: GpxReadingRecord[]; descartados: number } {
  const conhecido = (chave: string | null, ts: string): boolean => {
    if (chave === null) return false;
    const ultima = ultimaPorChave.get(chave);
    return ultima !== undefined && new Date(ts).getTime() <= ultima.getTime();
  };
  const landing = input.landing.filter((l) => !conhecido(chaveDoNome(l.instrumento), l.dataHoraLeitura));
  const readings = input.readings.filter((r) => !conhecido(`${r.dam}|${r.code}`, r.ts));
  return { landing, readings, descartados: input.landing.length - landing.length };
}

// ── Busca na v2 ──────────────────────────────────────────────────────────────────────

/** Registro com o mínimo para virar landing: nome e instante parseável. */
function registroValido(rec: unknown): rec is V2MonitoramentoRaw {
  if (rec === null || typeof rec !== 'object') return false;
  const r = rec as { instrumento?: unknown; dataHoraLeitura?: unknown };
  return typeof r.instrumento === 'string' && r.instrumento.length > 0
    && typeof r.dataHoraLeitura === 'string' && Number.isFinite(Date.parse(r.dataHoraLeitura));
}

function nomeDe(rec: unknown): string {
  const nome = rec !== null && typeof rec === 'object' ? (rec as { instrumento?: unknown }).instrumento : undefined;
  return typeof nome === 'string' && nome.length > 0 ? nome : '(sem nome)';
}

/** Tira a key de um texto qualquer — nunca deveria estar lá; aqui é a última barreira. */
function semChave(texto: string, apiKey: string): string {
  return apiKey ? texto.split(apiKey).join('***') : texto;
}

/** Timeout por requisição da busca por combo. A rodada inteira hoje leva ~22 s sequencial. */
const TIMEOUT_REQUISICAO_MS = 10_000;
/** Requisições simultâneas à v2. Baixo de propósito: a GPX não documenta rate limit. */
const CONCORRENCIA_PADRAO = 3;

export interface BuscaCombosDeps {
  apiKey: string;
  dataDe: string; // dd-MM-yyyy
  dataAte: string; // dd-MM-yyyy
  combos: readonly Combo[];
  fetchFn?: typeof fetch;
  baseUrl?: string;
  area?: string;
  timeoutMs?: number;
  concorrencia?: number;
  /**
   * Para de disparar requisições novas depois da primeira falha (as que já estão em voo
   * terminam). É o contrato do `fetchMonitoramentoV2`: o backfill pede centenas de
   * períodos, e um 403 de credencial não pode virar 21 GETs por período.
   */
  pararNaPrimeiraFalha?: boolean;
}

/** Combo que não respondeu: motivo com endpoint e status, nunca a key. */
export interface FalhaCombo {
  combo: Combo;
  motivo: string;
}

export interface ResultadoBusca {
  landing: LandingRow[];
  readings: GpxReadingRecord[];
  /** Registros que entraram no landing mas não renderam leitura curada, com o motivo. */
  ignorados: { instrumento: string; motivo: string }[];
  falhas: FalhaCombo[];
  /** Combos que responderam 204 — sem leitura no período OU data que a v2 não entendeu. */
  vazios: Combo[];
}

type ResultadoCombo =
  | { tipo: 'dados'; registros: unknown[] }
  | { tipo: 'vazio' }
  | { tipo: 'falha'; motivo: string };

/** Um GET de um combo, com timeout que cobre também a leitura do corpo. */
async function buscarUmCombo(
  combo: Combo,
  deps: Required<Pick<BuscaCombosDeps, 'apiKey' | 'dataDe' | 'dataAte' | 'baseUrl' | 'area' | 'timeoutMs'>>
    & { fetchFn: typeof fetch },
): Promise<ResultadoCombo> {
  // `instrumento=` VAZIO = curinga (todos daquela estrutura×tipo). A key vai no header.
  const endpoint =
    `${deps.baseUrl}/monitoramento/area/${encodeURIComponent(deps.area)}` +
    `/estrutura/${combo.estrutura}/tipo/${combo.tipo}` +
    `?instrumento=&dataDe=${deps.dataDe}&dataAte=${deps.dataAte}`;

  const controle = new AbortController();
  // `timeoutMs` 0 = sem prazo (contrato antigo do `fetchMonitoramentoV2`, usado no backfill).
  const timer = deps.timeoutMs > 0 ? setTimeout(() => controle.abort(), deps.timeoutMs) : undefined;
  try {
    let res: Response;
    try {
      res = await deps.fetchFn(endpoint, {
        method: 'GET',
        headers: { 'X-API-Key': deps.apiKey },
        signal: controle.signal,
      });
    } catch (e) {
      // Rede/timeout: contexto = endpoint (contém estrutura/tipo) + msg original; nunca a key.
      const causa = controle.signal.aborted ? `timeout após ${deps.timeoutMs} ms` : (e as Error).message;
      return { tipo: 'falha', motivo: `GET ${endpoint} falhou (rede): ${causa}` };
    }

    if (res.status === 204) return { tipo: 'vazio' };
    if (!res.ok) {
      // Credencial (401/403) / outro: status + endpoint; corpo e key ficam de fora. O corpo
      // não lido é descartado — no Deno, corpo pendurado segura o recurso da conexão.
      await res.body?.cancel().catch(() => undefined);
      return { tipo: 'falha', motivo: `GET ${endpoint} → HTTP ${res.status}` };
    }

    let data: unknown;
    try {
      data = await res.json();
    } catch (e) {
      const causa = controle.signal.aborted ? `timeout após ${deps.timeoutMs} ms` : 'corpo JSON inválido';
      return { tipo: 'falha', motivo: `GET ${endpoint} → ${causa}${controle.signal.aborted ? '' : ` (${(e as Error).message})`}` };
    }
    if (!Array.isArray(data)) {
      return { tipo: 'falha', motivo: `GET ${endpoint} → corpo inesperado (esperava array de leituras)` };
    }
    return { tipo: 'dados', registros: data };
  } finally {
    if (timer !== undefined) clearTimeout(timer);
  }
}

/**
 * GET da v2 por combo, com falha ISOLADA, timeout e concorrência limitada — GUPM-308.
 *
 * SÓ GET; `X-API-Key` no header (jamais na URL). Um combo que falha (rede, timeout, HTTP ≠
 * 2xx, corpo inválido) entra em `falhas` e os outros seguem: em 20–21/08/2026 a v2 devolveu
 * 502/408 no primeiro dos 21 endpoints e a busca, que lançava, custou 18 h sem inserir nada.
 *
 * Combo que falhou não entra no lote — logo os instrumentos dele não são julgados nesta
 * rodada e não viram "sem sinal" por falha nossa. O resultado sai na ordem de `combos`,
 * qualquer que seja a ordem em que as respostas chegaram.
 */
export async function buscarCombosV2(deps: BuscaCombosDeps): Promise<ResultadoBusca> {
  const cfg = {
    apiKey: deps.apiKey,
    dataDe: deps.dataDe,
    dataAte: deps.dataAte,
    fetchFn: deps.fetchFn ?? fetch,
    baseUrl: deps.baseUrl ?? V2_BASE,
    area: deps.area ?? 'UPM',
    timeoutMs: deps.timeoutMs ?? TIMEOUT_REQUISICAO_MS,
  };
  const concorrencia = Math.max(1, deps.concorrencia ?? CONCORRENCIA_PADRAO);

  // Posição = índice do combo; combo não pedido (parada na 1ª falha) fica `undefined`.
  const resultados: (ResultadoCombo | undefined)[] = new Array(deps.combos.length);
  let proximo = 0;
  let falhou = false;
  const trabalhador = async (): Promise<void> => {
    while (proximo < deps.combos.length && !(deps.pararNaPrimeiraFalha && falhou)) {
      const i = proximo++;
      const r = await buscarUmCombo(deps.combos[i]!, cfg);
      resultados[i] = r;
      if (r.tipo === 'falha') falhou = true;
    }
  };
  await Promise.all(Array.from({ length: Math.min(concorrencia, deps.combos.length) }, trabalhador));

  const out: ResultadoBusca = { landing: [], readings: [], ignorados: [], falhas: [], vazios: [] };
  deps.combos.forEach((combo, i) => {
    const r = resultados[i];
    if (r === undefined) return;
    if (r.tipo === 'vazio') {
      out.vazios.push(combo);
      return;
    }
    if (r.tipo === 'falha') {
      // Defesa extra: o motivo nunca carrega a key — mas `res.json()` de um proxy que ecoe
      // cabeçalhos poria um trecho do corpo na mensagem de erro.
      out.falhas.push({ combo, motivo: semChave(r.motivo, cfg.apiKey) });
      return;
    }
    for (const rec of r.registros) {
      // Registro sem a forma mínima (null no array, sem nome, sem data parseável) derrubaria
      // a rodada inteira no mapper ou no NOT NULL do landing — o padrão de 20–21/08. Vai
      // para `ignorados`, e os registros bons do mesmo combo seguem.
      if (!registroValido(rec)) {
        out.ignorados.push({ instrumento: nomeDe(rec), motivo: 'registro malformado (sem instrumento ou dataHoraLeitura válida)' });
        continue;
      }
      // Landing primeiro e sempre: é a camada que não pode perder registro.
      out.landing.push(mapRecordToLanding(rec, cfg.area, combo));
      try {
        out.readings.push(...mapRecordToReadings(rec));
      } catch (e) {
        // Sem barragem/código confiáveis não há como resolver o instrumento — fica só no
        // landing, reportado. Um nome estranho no meio do lote não pode custar os
        // registros bons que vieram com ele (GUPM-226).
        out.ignorados.push({ instrumento: rec.instrumento, motivo: (e as Error).message });
      }
    }
  });
  return out;
}

/**
 * GET batch da v2 (por estrutura×tipo, `instrumento=` vazio = todos daquela combinação),
 * no contrato antigo: sequencial e **lançando na primeira falha**, com o erro contendo
 * endpoint e status, sem a key.
 *
 * Mantido para o script local e o backfill, que preferem parar a seguir com buraco. A
 * ingestão agendada usa `buscarCombosV2` (GUPM-308), que isola a falha por combo.
 */
export async function fetchMonitoramentoV2(
  deps: FetchMonitoramentoDeps,
): Promise<{
  landing: LandingRow[];
  readings: GpxReadingRecord[];
  /** Registros que entraram no landing mas não renderam leitura curada, com o motivo. */
  ignorados: { instrumento: string; motivo: string }[];
}> {
  const estruturas = deps.estruturas ?? ESTRUTURAS;
  const tipos = deps.tipos ?? TIPOS_LEITURA;
  const out = await buscarCombosV2({
    apiKey: deps.apiKey,
    dataDe: deps.dataDe,
    dataAte: deps.dataAte,
    fetchFn: deps.fetchFn,
    baseUrl: deps.baseUrl,
    area: deps.area,
    combos: estruturas.flatMap((estrutura) => tipos.map((tipo) => ({ estrutura, tipo }))),
    concorrencia: 1,
    pararNaPrimeiraFalha: true,
    // Backfill pede meses de uma vez: o corpo é grande e lento. Sem prazo, como sempre foi
    // — um teto aqui reprovaria um mês grande que hoje passa.
    timeoutMs: 0,
  });
  if (out.falhas.length > 0) throw new Error(out.falhas[0]!.motivo);
  return { landing: out.landing, readings: out.readings, ignorados: out.ignorados };
}
